import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from 'components/Button';
import IconButton from 'components/IconButton';
import { endTrial, pauseAccount } from 'services/account.service';
import {
  loadAccount,
  loadUpcomingInvoice,
  loadUsage,
} from 'store/modules/settings/actions';
import ClearIcon from '@mui/icons-material/Clear';
import * as Sentry from '@sentry/browser';
import { toMoney } from 'utils';
import cookie from 'utils/cookie';
import toast from 'utils/toast';
import useAlert from 'hooks/useAlert';

const Root = styled(Box)`
  top: 0;
  margin: 0;
  position: relative;
  clear: both;
  padding-top: 10px;
  padding-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default () => {
  const dispatch = useDispatch();
  const alert = useAlert();
  const [t] = useTranslation(['settings']);
  const [hideBanner, setHideBanner] = useState(
    cookie.get('hide_upgrade_banner') === '1',
  );
  const account = useSelector((s) => s.settings.account);
  const { usage, upcomingInvoice } = account;
  const amount = upcomingInvoice ? upcomingInvoice.amount_due : 0;

  useEffect(() => {
    dispatch(loadUsage(account.account_id));
  }, []);

  useEffect(() => {
    if (account.hash) {
      dispatch(loadUpcomingInvoice(account.stripe_id)).catch((e) =>
        console.log(e),
      );
    }
  }, [account.subscriptions]);

  const onClose = (e) => {
    e.preventDefault();
    cookie.set('hide_upgrade_banner', '1', { path: '/' });
    setHideBanner(true);
  };

  const onResume = () => {
    alert.confirm({
      title: t('restart-plan', 'Restart Your Plan'),
      description: t(
        'restart-plan-message',
        'We will gladly Resume your billing if you need to resume your account. You can pause your account at any point.',
      ),
      confirmationText: t('restart-plan', 'Restart Plan'),
      cancellationText: t('common:cancel', 'Cancel'),
      onConfirm: () => {
        return pauseAccount(0)
          .then(() => {
            toast.success(
              t('account-resume-success', 'Your account has been resumed.'),
            );
          })
          .then(() => dispatch(loadAccount(account.account_id)))
          .catch((e) => toast.error(e.message));
      },
    });
  };

  const openIntercom = () => {
    if (window.Intercom) {
      window.Intercom('show');
    }
  };

  const closeAction = (
    <IconButton
      sx={{ position: 'absolute', right: 10, top: 5 }}
      onClick={onClose}
      icon={<ClearIcon />}
    />
  );

  if (hideBanner) return null;

  if (account.account_enabled === '0') {
    return (
      <Root sx={{ bgcolor: '#FFB800' }}>
        <Typography fontWeight={600}>
          Your account is suspended. Please contact support.
        </Typography>
        <Button
          onClick={openIntercom}
          sx={{
            'bgcolor': 'background.paper',
            'color': 'text.primary',
            'ml': 1,
            '&:hover': {
              bgcolor: 'background.paper',
            },
          }}
        >
          Contact support
        </Button>
        {closeAction}
      </Root>
    );
  }

  if (account.account_is_paused === '1') {
    return (
      <Root sx={{ bgcolor: '#8235F4', color: 'primary.contrastText' }}>
        <Typography fontWeight={600} sx={{ color: 'inherit' }}>
          You have paused your account. Reactivate to continue sending.
        </Typography>
        <Button
          sx={{
            'bgcolor': 'background.paper',
            'color': 'text.primary',
            'ml': 1,
            '&:hover': {
              bgcolor: 'background.paper',
            },
          }}
          onClick={onResume}
        >
          Reactivate
        </Button>
        {closeAction}
      </Root>
    );
  }
  return null;
};
